import React, {
  useState,
  useEffect,
  useCallback,
  FunctionComponent,
  Fragment,
} from "react";
import { DatePicker, Row, Col, Button, Collapse, Select, Radio, Tag, Spin } from "antd";
import PropTypes from "prop-types";
import moment, { Moment } from "moment";
import { connect } from "react-redux";
import fetchProcessedAdsByCluster, {
  FetchProcessedAdsByCluster,
} from "../../redux/actions/reports/fetchProcessedAdsByCluster";
import fetchProcessedAdsByPartner, {
  FetchProcessedAdsByPartner,
} from "../../redux/actions/reports/fetchProcessedAdsByPartner";
import ClusterTable from "./ClusterTable";
import {
  ProcessedAdsByClusterRecord,
  ProcessedAdsByPartnerRecord,
} from "../../redux/types/reportTypes";
import "./index.scss";
import PartnerTable from "./PartnerTable";
import { StoreType } from "../../redux/store";
import PartnerCSV from "./PartnerCSV";
import ClusterCSV from "./ClusterCSV";

type RecordOptions = ProcessedAdsByClusterRecord | ProcessedAdsByPartnerRecord;
export const rowClassName = (record: RecordOptions): string => {
  let className = "";
  if (record.difference < 0) className += "down-diff";
  else if (record.difference > 0) className += "up-diff";
  if (record.percentualDifference < 0) className += " down-perc-diff";
  else if (record.percentualDifference > 0) className += " up-perc-diff";
  return className;
};

type DateRangeComparisonProps = {
  fetchClusters: FetchProcessedAdsByCluster;
  fetchPartners: FetchProcessedAdsByPartner;
  clusterLoading: boolean;
  clusterRecords: Array<ProcessedAdsByClusterRecord>;
  partnerLoading: boolean;
  partnerRecords: Array<ProcessedAdsByPartnerRecord>;
};

const initialFilters = () => {
  const period2EndDate = moment();
  const period2StartDate = moment().add(-1, "month");
  const period1StartDate = moment().add(-1, "year").add(-1, "month");
  const period1EndDate = moment().add(-1, "year");
  return {
    period1StartDate,
    period1EndDate,
    period2StartDate,
    period2EndDate,
  };
};

export const DateRangeComparison: FunctionComponent<DateRangeComparisonProps> = ({
  fetchClusters,
  fetchPartners,
  clusterLoading,
  clusterRecords,
  partnerLoading,
  partnerRecords,
}: DateRangeComparisonProps) => {
  const [period1StartDate, setPeriod1StartDate] = useState<Moment>(moment());
  const [period1EndDate, setPeriod1EndDate] = useState<Moment>(moment());
  const [period2StartDate, setPeriod2StartDate] = useState<Moment>(moment());
  const [period2EndDate, setPeriod2EndDate] = useState<Moment>(moment());
  const [clusters, setClusters] = useState<Array<string>>();
  const [partners, setPartners] = useState<Array<number>>();
  const [origami, setOrigami] = useState<string>();

  const setInitialState = useCallback(() => {
    const initial = initialFilters();
    fetchClusters(initial);
    fetchPartners(initial);
    setPeriod1StartDate(initial.period1StartDate);
    setPeriod1EndDate(initial.period1EndDate);
    setPeriod2StartDate(initial.period2StartDate);
    setPeriod2EndDate(initial.period2EndDate);
    setClusters([]);
    setPartners([]);
    setOrigami("");
  }, [fetchClusters, fetchPartners]);

  useEffect(() => {
    setInitialState();
  }, [setInitialState]);

  const onCalendarChange = (kind: string) => (dates: any) => {
    const [startDate, endDate] = dates as [Moment, Moment];
    if (kind === "initial") {
      setPeriod1StartDate(startDate);
      setPeriod1EndDate(endDate);
    } else if (kind === "final") {
      setPeriod2StartDate(startDate);
      setPeriod2EndDate(endDate);
    }
  };

  const onClick = () => {
    fetchClusters(filters());
    fetchPartners(filters());
  };

  const filters = () => ({
    period1StartDate,
    period1EndDate,
    period2StartDate,
    period2EndDate,
    clusters,
    partners,
    origami,
  });

  const clusterOptions = () =>
    clusterRecords.map((record) => (
      <Select.Option key={record.cluster} value={record.cluster}>
        {record.cluster}
      </Select.Option>
    ));

  const clusterExtra = () => {
    if (clusterLoading) return <Spin />;
    return (
      <Fragment>
        <Tag color="geekblue">{`${clusterRecords.length} Result(s)`}</Tag>
        <ClusterCSV data={clusterRecords} />
      </Fragment>
    );
  };

  const partnerOptions = () =>
    partnerRecords.map((record) => (
      <Select.Option key={record.publisher} value={record.publisher}>
        {record.name}
      </Select.Option>
    ));

  const partnerExtra = () => {
    if (clusterLoading) return <Spin />;
    return (
      <Fragment>
        <Tag color="geekblue">{`${partnerRecords.length} Result(s)`}</Tag>
        <PartnerCSV data={partnerRecords} />
      </Fragment>
    );
  };

  return (
    <div className="simple-dashboard">
      <Row gutter={16}>
        <Col span={6}>
          <DatePicker.RangePicker
            allowClear={false}
            onCalendarChange={onCalendarChange("initial")}
            value={[period1StartDate, period1EndDate]}
          />
        </Col>
        <Col span={6}>
          <DatePicker.RangePicker
            allowClear={false}
            onCalendarChange={onCalendarChange("final")}
            value={[period2StartDate, period2EndDate]}
          />
        </Col>
        <Col span={2}>
          <Button type="primary" onClick={onClick}>
            Filter
          </Button>
        </Col>
        <Col span={2}>
          <Button onClick={setInitialState}>Clear</Button>
        </Col>
      </Row>
      <Collapse expandIconPosition="right" className="filters-collapse">
        <Collapse.Panel header="Filters" key="1">
          <Row gutter={16} className="filters-container">
            <Col span={8}>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Select Clusters"
                onChange={(value) => setClusters(value)}
                value={clusters}
              >
                {clusterOptions()}
              </Select>
            </Col>
            <Col span={8}>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Select Partners"
                onChange={(value) => setPartners(value)}
                value={partners}
              >
                {partnerOptions()}
              </Select>
            </Col>
            <Col span={8}>
              <Radio.Group
                defaultValue=""
                buttonStyle="solid"
                value={origami}
                onChange={(event) => setOrigami(event.target.value)}
              >
                <Radio.Button value="">Both</Radio.Button>
                <Radio.Button value="0">Legacy</Radio.Button>
                <Radio.Button value="1">Origami</Radio.Button>
              </Radio.Group>
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>
      <div className="accordion-container">
        <Collapse defaultActiveKey={["1", "2"]}>
          <Collapse.Panel
            header="Processed Ads by Cluster"
            key="1"
            extra={clusterExtra()}
          >
            <ClusterTable records={clusterRecords} loading={clusterLoading} />
          </Collapse.Panel>
          <Collapse.Panel
            header="Processed Ads by Partner"
            key="2"
            extra={partnerExtra()}
          >
            <PartnerTable records={partnerRecords} loading={partnerLoading} />
          </Collapse.Panel>
        </Collapse>
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  fetchClusters: fetchProcessedAdsByCluster,
  fetchPartners: fetchProcessedAdsByPartner,
};

const mapStateToProps = ({
  reports: {
    processed_ads_by_cluster: { loading: clusterLoading, records: clusterRecords },
    processed_ads_by_partner: { loading: partnerLoading, records: partnerRecords },
  },
}: StoreType) => ({ clusterLoading, clusterRecords, partnerLoading, partnerRecords });

DateRangeComparison.propTypes = {
  fetchClusters: PropTypes.func.isRequired,
  fetchPartners: PropTypes.func.isRequired,
  clusterLoading: PropTypes.bool.isRequired,
  clusterRecords: PropTypes.array.isRequired,
  partnerLoading: PropTypes.bool.isRequired,
  partnerRecords: PropTypes.array.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(DateRangeComparison);
